import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../pages/AdminDashboard.css";
import { API_BASE_URL } from "../config/api";

export default function AdminDashboard() {
    const [activeTab, setActiveTab] = useState("add");
    const [product, setProduct] = useState({
        name: "",
        description: "",
        category: "",
        type: "simple",
        sku: "",
        image: null,
    });

    const [sizes, setSizes] = useState({
        quarter: { price: "", salePrice: "", sku: "" },
        gallon: { price: "", salePrice: "", sku: "" },
        drum: { price: "", salePrice: "", sku: "" },
    });

    const [colors, setColors] = useState([{ name: "", hex: "" }]);
    const [products, setProducts] = useState([]);
    const [editingId, setEditingId] = useState(null);

    // ✅ Bulk CSV upload state
    const [bulkFile, setBulkFile] = useState(null);

    const navigate = useNavigate();

    const fetchProducts = async () => {
        try {
            const token = localStorage.getItem("admin_token");
            const res = await axios.get(`${API_BASE_URL}/api/products`, {
                headers: { Authorization: token },
            });
            setProducts(res.data);
        } catch (err) {
            console.log(err);
            alert("Error fetching products");
        }
    };

    useEffect(() => {
        if (activeTab === "view") fetchProducts();
    }, [activeTab]);

    const [filterCategory, setFilterCategory] = useState("all");

    const submitProduct = async (e) => {
        e.preventDefault();
        const formData = new FormData();

        if (product.name) {
            const slug = product.name.toLowerCase().replace(/\s+/g, "-");
            formData.append("slug", slug);
        }

        for (let key in product) {
            if (product[key]) formData.append(key, product[key]);
        }

        formData.append(
            "sizes",
            JSON.stringify([
                {
                    size: "quarter",
                    price: sizes.quarter.price,
                    salePrice: sizes.quarter.salePrice,
                    sku: sizes.quarter.sku,
                },
                {
                    size: "gallon",
                    price: sizes.gallon.price,
                    salePrice: sizes.gallon.salePrice,
                    sku: sizes.gallon.sku,
                },
                {
                    size: "drum",
                    price: sizes.drum.price,
                    salePrice: sizes.drum.salePrice,
                    sku: sizes.drum.sku,
                },
            ])
        );

        formData.append("colors", JSON.stringify(colors.filter((c) => c.hex)));

        try {
            const token = localStorage.getItem("admin_token");
            if (editingId) {
                const res = await axios.put(
                    `${API_BASE_URL}/api/products/update/${editingId}`,
                    formData,
                    { headers: { Authorization: token, "Content-Type": "multipart/form-data" } }
                );
                if (res.data.success) {
                    alert("Product Updated!");
                    setEditingId(null);
                }
            } else {
                const res = await axios.post(
                    `${API_BASE_URL}/api/products/add`,
                    formData,
                    { headers: { Authorization: token, "Content-Type": "multipart/form-data" } }
                );
                if (res.data.success) {
                    alert("Product Added!");
                }
            }

            setProduct({
                name: "",
                description: "",
                category: "",
                type: "simple",
                sku: "",
                image: null,
            });
            setSizes({
                quarter: { price: "", salePrice: "", sku: "" },
                gallon: { price: "", salePrice: "", sku: "" },
                drum: { price: "", salePrice: "", sku: "" },
            });
            setColors([{ name: "", hex: "" }]);

            fetchProducts();
            setActiveTab("view");
        } catch (err) {
            console.log(err);
            alert("Error submitting product");
        }
    };

    const deleteProduct = async (id) => {
        if (!window.confirm("Are you sure you want to delete this product?")) return;
        try {
            const token = localStorage.getItem("admin_token");
            await axios.delete(`${API_BASE_URL}/api/products/delete/${id}`, {
                headers: { Authorization: token },
            });
            alert("Product deleted!");
            setProducts(products.filter((p) => p._id !== id));
        } catch (err) {
            console.log(err);
            alert("Error deleting product");
        }
    };

    const editProduct = (p) => {
        setEditingId(p._id);
        setProduct({
            name: p.name,
            description: p.description,
            category: p.category,
            type: p.type,
            sku: p.sku,
            image: null,
        });

        const loadedSizes = { quarter: {}, gallon: {}, drum: {} };
        p.sizes.forEach((s) => {
            loadedSizes[s.size] = { price: s.price, salePrice: s.salePrice, sku: s.sku };
        });
        setSizes(loadedSizes);

        setColors(p.colors && p.colors.length > 0 ? p.colors : [{ name: "", hex: "" }]);
        setActiveTab("add");
    };

    const handleLogout = () => {
        localStorage.removeItem("admin_token");
        navigate("/");
    };

    // ✅ Bulk upload handler
    const uploadBulkProducts = async () => {
        if (!bulkFile) {
            alert("Please select a CSV file first");
            return;
        }

        const formData = new FormData();
        formData.append("file", bulkFile);

        try {
            const token = localStorage.getItem("admin_token");
            const res = await axios.post(
                `${API_BASE_URL}/api/products/bulk-upload`,
                formData,
                {
                    headers: {
                        Authorization: token,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            alert(`Uploaded ${res.data.inserted} products successfully`);
            setBulkFile(null);
            fetchProducts();
            setActiveTab("view");
        } catch (err) {
            console.log(err);
            alert("Bulk upload failed");
        }
    };

    const filteredProducts =
        filterCategory === "all"
            ? products
            : products.filter((p) => p.category === filterCategory);

    const getImageUrl = (imagePath) => {
        if (!imagePath) return "";
        if (/^https?:\/\//i.test(imagePath)) return imagePath;
        return `${API_BASE_URL}${imagePath.startsWith("/") ? imagePath : `/${imagePath}`}`;
    };

    return (
        <div className="admin-dashboard">
            <div className="sidebar">
                <h2>Admin Panel</h2>
                <ul>
                    <li
                        className={activeTab === "add" ? "active" : ""}
                        onClick={() => {
                            setActiveTab("add");
                            setEditingId(null);
                        }}
                    >
                        Add Product
                    </li>

                    <li
                        className={activeTab === "view" ? "active" : ""}
                        onClick={() => setActiveTab("view")}
                    >
                        View Products
                    </li>

                    {/* ✅ NEW TAB */}
                    <li
                        className={activeTab === "bulk" ? "active" : ""}
                        onClick={() => setActiveTab("bulk")}
                    >
                        Bulk Upload
                    </li>

                    <li
                        onClick={handleLogout}
                        style={{ marginTop: "auto", color: "#e74c3c", cursor: "pointer" }}
                    >
                        Logout
                    </li>
                </ul>
            </div>

            <div className="dashboard-content">
                {activeTab === "add" && (
                    <div>
                        <h2>{editingId ? "Edit Product" : "Add New Product"}</h2>
                        <form onSubmit={submitProduct} className="dashboard-form">
                            <div className="form-group">
                                <label>Product Name</label>
                                <input
                                    type="text"
                                    value={product.name}
                                    onChange={(e) => setProduct({ ...product, name: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={product.description}
                                    onChange={(e) =>
                                        setProduct({ ...product, description: e.target.value })
                                    }
                                />
                            </div>

                            <div className="form-group">
                                <label>Category</label>
                                <select
                                    value={product.category}
                                    onChange={(e) =>
                                        setProduct({ ...product, category: e.target.value })
                                    }
                                >
                                    <option value="">Select Category</option>
                                    <option value="Glamur Paints">Glamur Paints</option>
                                    <option value="Dulux Paints">Dulux Paints</option>
                                    <option value="Corona Paints">Corona Paints</option>
                                    <option value="Diamond Paints">Diamond Paints</option>
                                    <option value="Brighto Paints">Brighto Paints</option>
                                    <option value="Nelson Paints">Nelson Paints</option>
                                    <option value="Gobi's Paints">Gobi's Paints</option>
                                    <option value="Berger Paints">Berger Paints</option>
                                    <option value="Kansai Paints">Kansai Paints</option>
                                    <option value="Reliance Paints">Reliance Paints</option>
                                    <option value="Reliable Paints">Reliable Paints</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Product Type</label>
                                <select
                                    value={product.type}
                                    onChange={(e) => setProduct({ ...product, type: e.target.value })}
                                >
                                    <option value="simple">Simple</option>
                                    <option value="variable">Variable</option>
                                </select>
                            </div>

                            <h4>Product Sizes | Prices | SKU</h4>
                            {["quarter", "gallon", "drum"].map((size) => (
                                <div className="form-group" key={size}>
                                    <label>{size.charAt(0).toUpperCase() + size.slice(1)}</label>

                                    <input
                                        type="number"
                                        placeholder="Regular Price"
                                        value={sizes[size].price}
                                        onChange={(e) =>
                                            setSizes({
                                                ...sizes,
                                                [size]: { ...sizes[size], price: e.target.value },
                                            })
                                        }
                                    />

                                    <input
                                        type="number"
                                        placeholder="Sale Price"
                                        value={sizes[size].salePrice}
                                        onChange={(e) =>
                                            setSizes({
                                                ...sizes,
                                                [size]: { ...sizes[size], salePrice: e.target.value },
                                            })
                                        }
                                    />

                                    <input
                                        type="text"
                                        placeholder="SKU"
                                        value={sizes[size].sku}
                                        onChange={(e) =>
                                            setSizes({
                                                ...sizes,
                                                [size]: { ...sizes[size], sku: e.target.value },
                                            })
                                        }
                                    />
                                </div>
                            ))}

                            <h3>Color Shades (HEX)</h3>
                            {colors.map((c, idx) => (
                                <div key={idx} className="form-group color-input">
                                    {/* Color Preview Card */}
                                    <div
                                        className="color-preview"
                                        style={{
                                            backgroundColor: /^#([0-9A-Fa-f]{6})$/.test(c.hex)
                                                ? c.hex
                                                : "#e5e7eb",
                                        }}
                                        title={c.hex || "No color"}
                                    />

                                    <input
                                        type="text"
                                        placeholder="Color Name"
                                        value={c.name}
                                        onChange={(e) => {
                                            const newColors = [...colors];
                                            newColors[idx].name = e.target.value;
                                            setColors(newColors);
                                        }}
                                    />

                                    <input
                                        type="text"
                                        placeholder="#FFFFFF"
                                        value={c.hex}
                                        onChange={(e) => {
                                            const newColors = [...colors];
                                            newColors[idx].hex = e.target.value;
                                            setColors(newColors);
                                        }}
                                        pattern="^#([0-9A-Fa-f]{6})$"
                                    />

                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newColors = colors.filter((_, i) => i !== idx);
                                            setColors(newColors.length ? newColors : [{ name: "", hex: "" }]);
                                        }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={() => setColors([...colors, { name: "", hex: "" }])}
                            >
                                Add Color
                            </button>

                            <div className="form-group">
                                <label>Product Image</label>
                                <input
                                    type="file"
                                    onChange={(e) =>
                                        setProduct({ ...product, image: e.target.files[0] })
                                    }
                                />
                            </div>

                            <button type="submit" className="submit-btn">
                                {editingId ? "Update Product" : "Add Product"}
                            </button>
                        </form>
                    </div>
                )}

                {activeTab === "bulk" && (
                    <div>
                        <h2>Bulk Product Upload (CSV)</h2>
                        <p>Upload a CSV with columns: name, description, category, type, sizes, colors</p>

                        <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => setBulkFile(e.target.files[0])}
                        />

                        <button
                            className="submit-btn"
                            style={{ marginTop: 10 }}
                            type="button"
                            onClick={uploadBulkProducts}
                        >
                            Upload CSV
                        </button>

                        <div style={{ marginTop: 12, fontSize: 13 }}>
                            {/* <b>CSV Example:</b>
                            <pre style={{ whiteSpace: "pre-wrap" }}>
                                {`name,description,category,type,sizes,colors
Royal Emulsion,Interior paint,Glamur Paints,simple,"[{""size"":""quarter"",""price"":1200,""salePrice"":1100,""sku"":""RE-Q""}]","[{""name"":""White"",""hex"":""#FFFFFF""}]"`
                                }
                            </pre> */}
                        </div>
                    </div>
                )}

                {activeTab === "view" && (
                    <div>
                        <h2>View Products</h2>

                        <div className="filter-container">
                            <label>Filter by Category:</label>
                            <select
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Categories</option>
                                <option value="Glamur Paints">Glamur Paints</option>
                                <option value="Dulux Paints">Dulux Paints</option>
                                <option value="Corona Paints">Corona Paints</option>
                                <option value="Diamond Paints">Diamond Paints</option>
                                <option value="Brighto Paints">Brighto Paints</option>
                                <option value="Nelson Paints">Nelson Paints</option>
                                <option value="Gobi's Paints">Gobi's Paints</option>
                                <option value="Berger Paints">Berger Paints</option>
                                <option value="Kansai Paints">Kansai Paints</option>
                                <option value="Reliance Paints">Reliance Paints</option>
                                <option value="Reliable Paints">Reliable Paints</option>
                            </select>
                        </div>

                        {products.length === 0 ? (
                            <p>No products found.</p>
                        ) : (
                            <div className="product-table-wrap">
                                <table className="product-table">
                                    <thead>
                                        <tr>
                                            <th>No</th>
                                            <th>Image</th>
                                            <th>Name</th>
                                            <th>Category</th>
                                            <th>SKU</th>
                                            <th className="colors-col">Colors</th>
                                            <th>Quarter</th>
                                            <th>Gallon</th>
                                            <th>Drum</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>

                                <tbody>
                                    {filteredProducts.map((p, index) => {
                                        const sizesData = { quarter: {}, gallon: {}, drum: {} };

                                        if (Array.isArray(p.sizes)) {
                                            p.sizes.forEach((s) => {
                                                sizesData[s.size] = s;
                                            });
                                        }

                                        return (
                                            <tr key={p._id}>
                                                <td>{index + 1}</td> {/* ✅ SERIAL NUMBER */}

                                                <td className="product-image-cell">
                                                    {p.image ? (
                                                        <img
                                                            src={getImageUrl(p.image)}
                                                            alt={p.name}
                                                            className="product-thumb"
                                                            loading="lazy"
                                                            decoding="async"
                                                        />
                                                    ) : (
                                                        <span className="product-image-empty">No Image</span>
                                                    )}
                                                </td>

                                                <td>{p.name}</td>
                                                <td>{p.category}</td>

                                                <td>
                                                    {p.sizes?.length
                                                        ? p.sizes
                                                            .map((s) => `${s.size}: ${s.sku || "N/A"}`)
                                                            .join(" | ")
                                                        : "-"}
                                                </td>

                                                <td className="colors-col">
                                                    {p.colors?.length
                                                        ? p.colors
                                                            .map((c) => `${c.name} (${c.hex})`)
                                                            .join(" | ")
                                                        : "-"}
                                                </td>

                                                <td>
                                                    {sizesData.quarter.price
                                                        ? `₨.${sizesData.quarter.price} (Sale: ₨.${sizesData.quarter.salePrice || 0})`
                                                        : "N/A"}
                                                </td>

                                                <td>
                                                    {sizesData.gallon.price
                                                        ? `₨.${sizesData.gallon.price} (Sale: ₨.${sizesData.gallon.salePrice || 0})`
                                                        : "N/A"}
                                                </td>

                                                <td>
                                                    {sizesData.drum.price
                                                        ? `₨.${sizesData.drum.price} (Sale: ₨.${sizesData.drum.salePrice || 0})`
                                                        : "N/A"}
                                                </td>

                                                <td>
                                                    <button
                                                        className="edit-btn"
                                                        onClick={() => editProduct(p)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="delete-btn"
                                                        onClick={() => deleteProduct(p._id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
}
